// Compare (spec/dashboard.md section 12.1): two ref pickers — a branch of the list or a typed sha —, Swap, and Compare,
// which puts both refs in the URL (`history?from=&to=`); the URL drives `GET …/compare?from=&to=&include=content`.
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { ops } from '../../api/operations';
import type { CommitDetail } from '../../api/types';
import { useAuth } from '../../auth/AuthContext';
import { ChangesGrid } from '../../history/ChangesGrid';
import type { ContentChange } from '../../history/changes';
import { useGame } from '../../shell/GameContext';
import { routes } from '../../shell/routes';
import { Button } from '../../ui/Button';
import { ArrowRightLeft, X } from '../../ui/icons';
import { Select, TextInput } from '../../ui/inputs';
import { useQuery } from '../../workspace/cache';
import { errorText } from './util';
import { LoadError, LoadingPane } from '../../ui/PageStates';
import { EmptyState } from '../../ui/Banner';

const SHA = '__sha__';

function RefPicker({ label, value, onChange }: { label: 'From' | 'To'; value: string; onChange: (v: string) => void }) {
  const { branches } = useGame();
  const isBranch = branches.some((b) => b.name === value);
  const [shaMode, setShaMode] = useState(!isBranch && value !== '');
  useEffect(() => {
    if (branches.some((b) => b.name === value)) setShaMode(false);
    else if (value !== '') setShaMode(true);
  }, [value, branches]);
  return (
    <div className="ref-picker">
      <span className="ref-label">{label}</span>
      <Select
        aria-label={label}
        value={shaMode ? SHA : value}
        onChange={(e) => {
          if (e.target.value === SHA) {
            setShaMode(true);
            onChange('');
          } else {
            setShaMode(false);
            onChange(e.target.value);
          }
        }}
      >
        {!shaMode && !isBranch && <option value={value}>Choose…</option>}
        {branches.map((b) => (
          <option key={b.id} value={b.name}>
            {b.name}
          </option>
        ))}
        <option value={SHA}>A commit sha…</option>
      </Select>
      {shaMode && <TextInput aria-label={`${label} commit`} value={value} onChange={(e) => onChange(e.target.value.trim())} placeholder="sha" className="mono" autoComplete="off" />}
    </div>
  );
}

export function ComparePanel({ branch, onClose }: { branch: string; onClose: () => void }) {
  const { client } = useAuth();
  const { org, game, branches } = useGame();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const urlFrom = params.get('from');
  const urlTo = params.get('to');
  const fallbackFrom = branches.find((b) => b.isDefault && b.name !== branch)?.name ?? branches.find((b) => b.name !== branch)?.name ?? branch;
  const [from, setFrom] = useState(urlFrom ?? fallbackFrom);
  const [to, setTo] = useState(urlTo ?? branch);
  // the URL is the truth: a back / forward or a deep link re-seeds the pickers
  useEffect(() => {
    if (urlFrom !== null) setFrom(urlFrom);
    if (urlTo !== null) setTo(urlTo);
  }, [urlFrom, urlTo]);
  const active = urlFrom && urlTo ? { from: urlFrom, to: urlTo } : null;
  const q = useQuery(active ? `branch:${game.id}:${branch}:history:compare:${active.from}..${active.to}` : null, () =>
    client.data(ops.compareCommits, { path: { org, game: game.slug, branch }, query: { from: active!.from, to: active!.to, include: 'content' } }) as unknown as Promise<CommitDetail>,
  );
  const compare = (f: string, t: string) => navigate(routes.compare(org, game.slug, branch, f, t));
  const swap = () => {
    setFrom(to);
    setTo(from);
    if (active) compare(to, from);
  };
  return (
    <section className="compare-panel" data-testid="compare-panel" aria-label="Compare">
      <div className="page-toolbar compare-pickers">
        <RefPicker label="From" value={from} onChange={setFrom} />
        <Button size="s" variant="ghost" onClick={swap} aria-label="Swap" icon={<ArrowRightLeft aria-hidden="true" />}>
          Swap
        </Button>
        <RefPicker label="To" value={to} onChange={setTo} />
        <Button size="s" variant="primary" onClick={() => compare(from, to)} disabled={!from || !to}>
          Compare
        </Button>
        <span className="spacer" />
        <Button size="s" variant="ghost" icon={<X aria-hidden="true" />} onClick={onClose} aria-label="Close compare">
          Close
        </Button>
      </div>
      {!active ? (
        <EmptyState icon={<ArrowRightLeft />} title="Compare two refs" body="Pick two refs, a branch or a commit sha, and Compare: the changes that lead from the first to the second." />
      ) : q.error ? (
        <LoadError>{errorText(q.error)}</LoadError>
      ) : !q.data ? (
        <LoadingPane rows={3} label="Comparing" />
      ) : (
        <ChangesGrid changes={q.data.changes as ContentChange[]} branch={branch} contentTruncated={q.data.contentTruncated ?? undefined} emptyMessage="The two refs hold the same content." />
      )}
    </section>
  );
}
